export type SlotId =
  | "trial-balance"
  | "gl-detail"
  | "po-accrual"
  | "inventory-valuation"
  | "ar-aging"
  | "ap-aging"
  | "payroll-earnings"
  | "employee-accruals"
  | "fa-depreciation"
  | "external-schedules";

export interface SlotColumn {
  key: string;
  label: string;
  required: boolean;
  kind: "string" | "number" | "date";
}

export interface SlotDef {
  id: SlotId;
  name: string;
  sageSource: string;
  expectedExtensions: ("xlsx" | "csv")[];
  /** Which recons depend on this slot having an active upload. */
  feedsRecons: string[];
  /** Columns we expect to find (header-match, case-insensitive). */
  columns: SlotColumn[];
  /** Optional worksheet name for XLSX. If omitted, first sheet. */
  sheetName?: string;
  /** One-line instructions shown in the UI. */
  howTo: string;
  phase: 1 | 2 | 4 | 5;
}

const N = (key: string, label: string, required = true): SlotColumn => ({
  key,
  label,
  required,
  kind: "number",
});
const S = (key: string, label: string, required = true): SlotColumn => ({
  key,
  label,
  required,
  kind: "string",
});
const D = (key: string, label: string, required = false): SlotColumn => ({
  key,
  label,
  required,
  kind: "date",
});

export const SLOT_DEFS: SlotDef[] = [
  {
    id: "trial-balance",
    name: "Trial Balance",
    sageSource: "GL Reports → Trial Balance by Period",
    expectedExtensions: ["csv", "xlsx"],
    feedsRecons: ["balance-sheet", "pl", "intercompany-cervos"],
    columns: [
      S("glAccount", "G/L Account"),
      S("description", "Description", false),
      N("beginningBalance", "Beginning Balance"),
      N("debit", "Period Debit"),
      N("credit", "Period Credit"),
      N("endingBalance", "Ending Balance"),
    ],
    howTo:
      "In Sage: Modules → General Ledger → Reports → Trial Balance. Set the period to the close month and export to CSV.",
    phase: 2,
  },
  {
    id: "gl-detail",
    name: "GL Detail",
    sageSource: "GL Reports → Detail Transaction Report",
    expectedExtensions: ["csv", "xlsx"],
    feedsRecons: ["intercompany-cervos", "inventory-reserve", "ar-allowance"],
    columns: [
      S("glAccount", "G/L Account"),
      D("postingDate", "Posting Date"),
      S("sourceJournal", "Source Journal"),
      S("reference", "Reference", false),
      S("comment", "Comment", false),
      N("debit", "Debit"),
      N("credit", "Credit"),
    ],
    howTo:
      "GL → Reports → Detail Transaction Report. Period = close month. Export CSV (do not filter — we'll filter in the app).",
    phase: 2,
  },
  {
    id: "po-accrual",
    name: "PO Accrual (Open POs Received Not Invoiced)",
    sageSource: "PO Reports → Open Purchase Order Report",
    expectedExtensions: ["xlsx", "csv"],
    feedsRecons: ["po-accrual"],
    sheetName: "accrd purchases",
    columns: [
      S("poNumber", "PO#"),
      S("vendorNumber", "Vendor #", false),
      S("vendorName", "Vendor", false),
      D("orderDate", "OrderDate", false),
      D("lastReceiptDate", "Last ReceiptDate", false),
      S("itemNumber", "Item#", false),
      S("itemDescription", "Item Desc", false),
      S("productLine", "Prod Line", false),
      S("glAccount", "GL#", false),
      N("qtyOrdered", "Qty Ordered", false),
      N("qtyReceived", "Qty Rec'd"),
      N("qtyInvoiced", "Qty Invoiced"),
      N("unitCost", "Unit Cost"),
      N("purchasesClearing", "Purchases Clearing", false),
    ],
    howTo:
      "PO → Reports → Open Purchase Order Report. Filter: 'Received Not Invoiced'. Period end = close date. Export XLSX.",
    phase: 1,
  },
  {
    id: "inventory-valuation",
    name: "Inventory Valuation",
    sageSource: "IM Reports → Inventory Valuation Report",
    expectedExtensions: ["xlsx", "csv"],
    feedsRecons: ["inventory", "inventory-reserve"],
    columns: [
      S("itemNumber", "Item #"),
      S("description", "Description", false),
      S("warehouse", "Warehouse", false),
      S("productLine", "Product Line", false),
      S("glAccount", "GL Account", false),
      N("qtyOnHand", "Qty on Hand"),
      N("avgCost", "Avg Cost", false),
      N("extendedValue", "Extended Value"),
    ],
    howTo:
      "Inventory → Reports → Valuation Report. Group by warehouse and GL. Export XLSX.",
    phase: 2,
  },
  {
    id: "ar-aging",
    name: "AR Aging",
    sageSource: "AR Reports → Aged Invoice Report (CSV, not PDF)",
    expectedExtensions: ["csv", "xlsx"],
    feedsRecons: ["ar-allowance", "ar-misc", "ar-orchard"],
    columns: [
      S("customerNumber", "Customer #"),
      S("customerName", "Customer Name", false),
      S("invoiceNumber", "Invoice #"),
      D("invoiceDate", "Invoice Date", false),
      D("dueDate", "Due Date", false),
      N("amount", "Amount"),
      N("current", "Current", false),
      N("days1To30", "1-30 Days", false),
      N("days31To60", "31-60 Days", false),
      N("days61To90", "61-90 Days", false),
      N("daysOver90", "Over 90 Days", false),
    ],
    howTo:
      "AR → Reports → Aged Invoice Report. Export CSV. (If you only have the PDF, open it in Excel — usually copies cleanly — and save as CSV.)",
    phase: 4,
  },
  {
    id: "ap-aging",
    name: "AP Aging",
    sageSource: "AP Reports → Aged Invoice Report (CSV)",
    expectedExtensions: ["csv", "xlsx"],
    feedsRecons: ["accrued-other", "customer-deposits"],
    columns: [
      S("vendorNumber", "Vendor #"),
      S("vendorName", "Vendor Name", false),
      S("invoiceNumber", "Invoice #"),
      D("invoiceDate", "Invoice Date", false),
      D("dueDate", "Due Date", false),
      N("amount", "Amount"),
    ],
    howTo: "AP → Reports → Aged Invoice Report. Export CSV.",
    phase: 4,
  },
  {
    id: "payroll-earnings",
    name: "Payroll Earnings by Employee",
    sageSource: "PR Reports → Earnings Report",
    expectedExtensions: ["xlsx", "csv"],
    feedsRecons: ["accrued-payroll", "accrued-401k-match", "accrued-commissions"],
    columns: [
      S("employeeNumber", "Employee #"),
      S("employeeName", "Employee Name", false),
      S("department", "Department", false),
      N("regularHours", "Regular Hours", false),
      N("regularEarnings", "Regular Earnings"),
      N("overtimeEarnings", "Overtime Earnings", false),
      N("grossPay", "Gross Pay"),
    ],
    howTo: "Payroll → Reports → Earnings Report. Period = close month. Export XLSX.",
    phase: 4,
  },
  {
    id: "employee-accruals",
    name: "Employee Accruals (Vacation/Sick)",
    sageSource: "PR Reports → Accrual Report",
    expectedExtensions: ["xlsx", "csv"],
    feedsRecons: ["accrued-vacation"],
    columns: [
      S("employeeNumber", "Employee #"),
      S("employeeName", "Employee Name", false),
      S("department", "Department", false),
      N("vacationHours", "Vacation Hours"),
      N("sickHours", "Sick Hours", false),
      N("hourlyRate", "Hourly Rate"),
    ],
    howTo: "Payroll → Reports → Accrual Report. Export XLSX.",
    phase: 2,
  },
  {
    id: "fa-depreciation",
    name: "Fixed Assets & Depreciation",
    sageSource: "Sage FAS → Depreciation Expense Report",
    expectedExtensions: ["xlsx", "csv"],
    feedsRecons: ["fixed-assets"],
    columns: [
      S("assetNumber", "Asset #"),
      S("description", "Description", false),
      S("glExpenseAccount", "Depreciation G/L", false),
      D("placedInService", "Placed In Service", false),
      N("cost", "Cost", false),
      N("accumulatedDepreciation", "Accum Depr", false),
      N("monthlyDepreciation", "Monthly Depreciation"),
    ],
    howTo: "FAS → Reports → Depreciation Expense. Period = close month. Export XLSX.",
    phase: 2,
  },
  {
    id: "external-schedules",
    name: "External Schedules (Loans, Leases, Insurance, Cervos)",
    sageSource: "Lender/insurance statements, lease amortization, Cervos close package",
    expectedExtensions: ["xlsx", "csv"],
    feedsRecons: [
      "berkshire-loans",
      "endocellutions-note",
      "lease-rou",
      "life-insurance",
      "captive",
      "cervos-investment",
      "intercompany-cervos",
    ],
    columns: [
      S("scheduleType", "Schedule Type"),
      S("lineDescription", "Line Description", false),
      S("glAccount", "GL Account", false),
      D("asOfDate", "As Of Date", false),
      N("amount", "Amount"),
    ],
    howTo:
      "Upload a consolidated workbook with one row per external item (Berkshire loan balance, lease expense, insurance premium, Cervos investment balance, etc.). Template available — ask Harlan.",
    phase: 4,
  },
];

export const SLOT_BY_ID: Record<SlotId, SlotDef> = Object.fromEntries(
  SLOT_DEFS.map((s) => [s.id, s]),
) as Record<SlotId, SlotDef>;

export type SlotStatus =
  | "missing"
  | "uploaded"
  | "validation_failed"
  | "superseded";

export interface SlotState {
  slotId: SlotId;
  status: SlotStatus;
  filename: string | null;
  rowCount: number | null;
  uploadedAt: string | null;
  uploadedBy: string | null;
  errors: string[];
}

export interface ParsedUpload {
  slotId: SlotId;
  filename: string;
  rows: Record<string, string | number | null>[];
}
